<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { Bell, CirclePlus, Cloudy, CopyDocument, DataAnalysis, Delete, Key, Link, Refresh, Warning } from "@element-plus/icons-vue";
import { api, jsonBody } from "../api";

interface Overview { accounts: number; domains: number; active: number; failed: number; runningJobs: number }
interface Account { id: string; name: string; accountId: string; status: string; hasResend: boolean; createdAt: string }
interface Domain { id: string; zone: string; mailboxDomain: string | null; webmailHost: string; adminEmail: string; adminLocalPart: string | null; brandName: string; accent: string; status: string; errorMessage: string | null; accountName: string }
interface Job { id: string; targetId: string; status: string; stage: string; progress: number; errorMessage: string | null; updatedAt: string }
type IntegrationStrategy = "round_robin" | "random" | "least_used";
interface IntegrationClient { id: string; name: string; tokenPrefix: string; allowedDomains: string[]; blockedDomains: string[]; defaultStrategy: IntegrationStrategy; expiresAt: string | null; lastUsedAt: string | null; createdAt: string; allocationCount: number }
interface ContactNotificationSettings { provider: "email_forward"; enabled: boolean; configured: boolean; forwardTo: string | null }

const loading = ref(true);
const overview = ref<Overview>({ accounts: 0, domains: 0, active: 0, failed: 0, runningJobs: 0 });
const accounts = ref<Account[]>([]);
const domains = ref<Domain[]>([]);
const jobs = ref<Job[]>([]);
const integrations = ref<IntegrationClient[]>([]);
const contactNotification = ref<ContactNotificationSettings>({ provider: "email_forward", enabled: false, configured: false, forwardTo: null });
const activeView = ref<"overview" | "domains" | "accounts" | "integrations" | "notifications" | "jobs">("overview");
const accountDialog = ref(false);
const domainDialog = ref(false);
const submitting = ref(false);
const integrationDialog = ref(false);
const editingIntegration = ref<IntegrationClient | null>(null);
const revealedIntegrationToken = ref("");
const accountForm = ref({ name: "", accountId: "", apiToken: "", resendApiKey: "" });
const domainForm = ref({ accountId: "", zone: "", adminEmail: "", adminLocalPart: "admin", brandName: "", accent: "#d9673f" });
const integrationForm = ref({ name: "Any Auto Register", defaultStrategy: "round_robin" as IntegrationStrategy, allowedDomains: "", blockedDomains: "", expiresInDays: "365" });
const recentDomains = computed(() => domains.value.slice(0, 5));
const viewTitle = computed(() => ({ overview: "运行总览", domains: "邮箱域名", accounts: "Cloudflare 账户", integrations: "邮箱集成", notifications: "联系通知", jobs: "装配任务" })[activeView.value]);

async function refresh() {
  loading.value = true;
  try {
    [overview.value, accounts.value, domains.value, jobs.value, integrations.value, contactNotification.value] = await Promise.all([
      api<Overview>("/api/v1/control/overview"), api<Account[]>("/api/v1/control/accounts"),
      api<Domain[]>("/api/v1/control/domains"), api<Job[]>("/api/v1/control/jobs"), api<IntegrationClient[]>("/api/v1/control/integrations"),
      api<ContactNotificationSettings>("/api/v1/control/contact-webhook"),
    ]);
    if (!domainForm.value.accountId && accounts.value[0]) domainForm.value.accountId = accounts.value[0].id;
  } catch (error) { ElMessage.error(error instanceof Error ? error.message : "加载失败"); }
  finally { loading.value = false; }
}

async function createAccount() {
  submitting.value = true;
  try {
    await api("/api/v1/control/accounts", { method: "POST", ...jsonBody({ ...accountForm.value, resendApiKey: accountForm.value.resendApiKey || undefined }) });
    accountDialog.value = false;
    accountForm.value = { name: "", accountId: "", apiToken: "", resendApiKey: "" };
    ElMessage.success("Cloudflare 账户已安全保存");
    await refresh();
  } catch (error) { ElMessage.error(error instanceof Error ? error.message : "创建失败"); }
  finally { submitting.value = false; }
}

async function createDomain() {
  submitting.value = true;
  try {
    await api("/api/v1/control/domains", { method: "POST", ...jsonBody({ ...domainForm.value, brandName: domainForm.value.brandName || undefined }) });
    domainDialog.value = false;
    ElMessage.success("域名装配任务已启动");
    await refresh();
  } catch (error) { ElMessage.error(error instanceof Error ? error.message : "装配启动失败"); }
  finally { submitting.value = false; }
}

async function retry(domain: Domain) {
  await ElMessageBox.confirm(`重新装配 ${domain.zone}？只会重试失败步骤。`, "确认重试", { type: "warning" });
  await api(`/api/v1/control/domains/${domain.id}/retry`, { method: "POST" });
  ElMessage.success("已重新排队");
  await refresh();
}

function policyEntries(value: string): string[] {
  return [...new Set(value.split(/[\s,;]+/).map((item) => item.trim().toLowerCase()).filter(Boolean))];
}

function strategyLabel(value: IntegrationStrategy): string {
  return ({ round_robin: "顺序轮询", random: "随机分配", least_used: "最少使用" })[value];
}

function openIntegration(client?: IntegrationClient) {
  editingIntegration.value = client ?? null;
  integrationForm.value = client ? {
    name: client.name,
    defaultStrategy: client.defaultStrategy,
    allowedDomains: client.allowedDomains.join("\n"),
    blockedDomains: client.blockedDomains.join("\n"),
    expiresInDays: "365",
  } : { name: "Any Auto Register", defaultStrategy: "round_robin", allowedDomains: "", blockedDomains: "", expiresInDays: "365" };
  integrationDialog.value = true;
}

async function saveIntegration() {
  submitting.value = true;
  try {
    const payload = {
      name: integrationForm.value.name,
      defaultStrategy: integrationForm.value.defaultStrategy,
      allowedDomains: policyEntries(integrationForm.value.allowedDomains),
      blockedDomains: policyEntries(integrationForm.value.blockedDomains),
    };
    if (editingIntegration.value) {
      await api(`/api/v1/control/integrations/${editingIntegration.value.id}`, { method: "PATCH", ...jsonBody(payload) });
      ElMessage.success("集成策略已更新");
    } else {
      const created = await api<{ token: string }>("/api/v1/control/integrations", {
        method: "POST",
        ...jsonBody({ ...payload, expiresInDays: integrationForm.value.expiresInDays === "never" ? null : Number(integrationForm.value.expiresInDays) }),
      });
      revealedIntegrationToken.value = created.token;
      ElMessage.success("Integration Token 已创建，请立即复制");
    }
    integrationDialog.value = false;
    await refresh();
  } catch (error) { ElMessage.error(error instanceof Error ? error.message : "保存集成失败"); }
  finally { submitting.value = false; }
}

async function revokeIntegration(client: IntegrationClient) {
  await ElMessageBox.confirm(`撤销 ${client.name}？连接它的自动化程序会立即停止读取邮件。`, "撤销邮箱集成", { type: "warning" });
  await api(`/api/v1/control/integrations/${client.id}`, { method: "DELETE" });
  ElMessage.success("邮箱集成已撤销");
  await refresh();
}

async function copy(value: string) {
  await navigator.clipboard.writeText(value);
  ElMessage.success("已复制到剪贴板");
}

onMounted(() => { void refresh(); });
</script>

<template>
  <div class="control-layout">
    <aside class="control-nav">
      <div class="nav-heading">OPERATIONS</div>
      <button :class="{ active: activeView === 'overview' }" @click="activeView = 'overview'"><DataAnalysis />总览</button>
      <button :class="{ active: activeView === 'domains' }" @click="activeView = 'domains'"><Link />域名</button>
      <button :class="{ active: activeView === 'accounts' }" @click="activeView = 'accounts'"><Cloudy />账户</button>
      <button :class="{ active: activeView === 'integrations' }" @click="activeView = 'integrations'"><Key />邮箱集成</button>
      <button :class="{ active: activeView === 'notifications' }" @click="activeView = 'notifications'"><Bell />联系通知</button>
      <button :class="{ active: activeView === 'jobs' }" @click="activeView = 'jobs'"><Refresh />任务</button>
      <div class="nav-note">
        <span>生产环境</span>
        <strong>边缘资源由主分支统一发布</strong>
      </div>
    </aside>

    <section class="workspace" v-loading="loading">
      <div class="page-heading">
        <div>
          <span class="eyebrow">CENTRAL COMMAND</span>
          <h1>{{ viewTitle }}</h1>
          <p>{{ activeView === 'integrations' ? '向自动化系统提供受策略约束的动态多域邮箱池。' : activeView === 'notifications' ? '把所有域名的官方联系邮件原样送达同一个管理员邮箱。' : '从一处观察资源、身份和邮件域的完整装配状态。' }}</p>
        </div>
        <div class="heading-actions">
          <button class="quiet-button" @click="refresh"><Refresh /> 刷新</button>
          <button v-if="activeView === 'accounts'" class="primary-button" @click="accountDialog = true"><CirclePlus /> 接入账户</button>
          <button v-else-if="activeView === 'integrations'" class="primary-button" @click="openIntegration()"><Key /> 创建集成</button>
          <button v-else-if="activeView === 'domains' || activeView === 'overview'" class="primary-button" @click="domainDialog = true"><CirclePlus /> 添加域名</button>
        </div>
      </div>

      <template v-if="activeView === 'overview'">
        <div class="metric-grid">
          <article class="metric-card ink"><span>ACTIVE DOMAINS</span><strong>{{ overview.active }}</strong><small>已完成 DNS、路由与 TLS</small></article>
          <article class="metric-card"><span>MANAGED DOMAINS</span><strong>{{ overview.domains }}</strong><small>跨 {{ overview.accounts }} 个 Cloudflare 账户</small></article>
          <article class="metric-card"><span>IN PROGRESS</span><strong>{{ overview.runningJobs }}</strong><small>持久化工作流正在执行</small></article>
          <article class="metric-card" :class="{ alert: overview.failed > 0 }"><span>NEEDS ATTENTION</span><strong>{{ overview.failed }}</strong><small>失败任务可安全重试</small></article>
        </div>
        <div class="split-grid">
          <article class="paper-card table-card">
            <div class="card-title"><div><span class="eyebrow">LATEST</span><h2>最近域名</h2></div><button class="text-button" @click="activeView = 'domains'">查看全部 →</button></div>
            <div class="domain-row" v-for="domain in recentDomains" :key="domain.id">
              <span class="domain-avatar" :style="{ background: domain.accent }">{{ domain.zone[0]?.toUpperCase() }}</span>
              <div><strong>{{ domain.zone }}</strong><small>{{ domain.webmailHost }}</small></div>
              <span class="status-pill" :class="domain.status">{{ domain.status }}</span>
            </div>
            <div v-if="!recentDomains.length" class="empty-state"><Link /><p>尚未装配域名</p><button class="text-button" @click="domainDialog = true">添加第一个域名</button></div>
          </article>
          <article class="paper-card topology-card">
            <span class="eyebrow">RESOURCE MAP</span><h2>共享数据面</h2>
            <div class="topology">
              <div class="topology-node core">MAIL<br>WORKER</div><span class="topology-line" />
              <div class="resource-stack"><span>D1</span><span>R2</span><span>KV</span><span>QUEUE</span></div>
            </div>
            <p>每个 Cloudflare 账户共享一套隔离数据面；所有邮件记录均绑定 domain_id。</p>
          </article>
        </div>
      </template>

      <article v-else-if="activeView === 'domains'" class="paper-card data-table-card">
        <div class="data-table-header"><span>域名 / 邮箱后缀</span><span>账户</span><span>管理员</span><span>状态</span><span /></div>
        <div class="data-table-row" v-for="domain in domains" :key="domain.id">
          <div><strong>{{ domain.zone }}</strong><small>@{{ domain.mailboxDomain || '检测中' }} · {{ domain.webmailHost }}</small></div>
          <span>{{ domain.accountName }}</span><span>{{ domain.adminLocalPart || 'admin' }}@{{ domain.mailboxDomain || domain.zone }}<small>登录：{{ domain.adminEmail }}</small></span>
          <span class="status-pill" :class="domain.status">{{ domain.status }}</span>
          <button v-if="domain.status === 'failed'" class="icon-button" :title="domain.errorMessage || '重试'" @click="retry(domain)"><Refresh /></button>
          <a v-else-if="domain.status === 'active'" class="icon-button" :href="`https://${domain.webmailHost}`" target="_blank" rel="noreferrer" title="打开独立邮箱系统"><Link /></a>
          <span v-else />
        </div>
        <div v-if="!domains.length" class="empty-state"><Link /><p>尚无域名</p></div>
      </article>

      <article v-else-if="activeView === 'accounts'" class="paper-card data-table-card">
        <div class="data-table-header account-cols"><span>账户</span><span>Account ID</span><span>Resend</span><span>状态</span></div>
        <div class="data-table-row account-cols" v-for="account in accounts" :key="account.id">
          <strong>{{ account.name }}</strong><code>{{ account.accountId }}</code><span>{{ account.hasResend ? '已配置' : '未配置' }}</span><span class="status-pill" :class="account.status">{{ account.status }}</span>
        </div>
        <div v-if="!accounts.length" class="empty-state"><Cloudy /><p>尚未接入 Cloudflare 账户</p></div>
      </article>

      <section v-else-if="activeView === 'integrations'" class="integration-workspace">
        <article class="integration-manifest">
          <div>
            <span class="eyebrow">DYNAMIC MAILBOX POOL</span>
            <h2>一个凭据，连接所有合规域名。</h2>
            <p>白名单为空时自动纳入当前与未来激活域名；黑名单始终优先。分配前会实时确认“接收邮件”和“无人收件”均已开启。</p>
          </div>
          <div class="manifest-route"><span>ANY AUTO REGISTER</span><i>→</i><strong>{{ integrations.length }} CLIENT{{ integrations.length === 1 ? '' : 'S' }}</strong><i>→</i><span>{{ overview.active }} ACTIVE DOMAINS</span></div>
        </article>

        <div v-if="revealedIntegrationToken" class="integration-token-reveal">
          <div><span>仅显示这一次 · 请保存到调用方</span><code>{{ revealedIntegrationToken }}</code></div>
          <button class="quiet-button" @click="copy(revealedIntegrationToken)"><CopyDocument /> 复制 Token</button>
        </div>

        <div class="integration-grid">
          <article v-for="client in integrations" :key="client.id" class="integration-client-card">
            <header><span class="integration-key-mark"><Key /></span><div><strong>{{ client.name }}</strong><code>{{ client.tokenPrefix }}••••••••</code></div><span class="status-pill active">active</span></header>
            <dl>
              <div><dt>默认策略</dt><dd>{{ strategyLabel(client.defaultStrategy) }}</dd></div>
              <div><dt>已分配</dt><dd>{{ client.allocationCount }} 个邮箱</dd></div>
              <div><dt>最后使用</dt><dd>{{ client.lastUsedAt ? new Date(client.lastUsedAt).toLocaleString() : '尚未调用' }}</dd></div>
              <div><dt>有效期</dt><dd>{{ client.expiresAt ? new Date(client.expiresAt).toLocaleDateString() : '永不过期' }}</dd></div>
            </dl>
            <div class="policy-lane"><span>ALLOW</span><div><code v-for="item in client.allowedDomains" :key="item">{{ item }}</code><em v-if="!client.allowedDomains.length">所有当前及未来域名</em></div></div>
            <div class="policy-lane blocked"><span>BLOCK</span><div><code v-for="item in client.blockedDomains" :key="item">{{ item }}</code><em v-if="!client.blockedDomains.length">无</em></div></div>
            <footer><button class="text-button" @click="openIntegration(client)">编辑策略</button><button class="danger-text" @click="revokeIntegration(client)"><Delete /> 撤销</button></footer>
          </article>
          <button class="integration-add-card" @click="openIntegration()"><CirclePlus /><strong>创建邮箱池客户端</strong><span>签发独立凭据并定义域名边界</span></button>
        </div>
      </section>

      <section v-else-if="activeView === 'notifications'" class="contact-notification-workspace">
        <article class="contact-manifest">
          <div>
            <span class="eyebrow">OFFICIAL CONTACT DESK</span>
            <h2>每一封联系邮件，都抵达同一张值班桌。</h2>
            <p>每个激活域名固定拥有 <code>contact@域名</code> 官方地址。原始邮件和附件保留在对应 Webmail，同时免费转发到统一管理员邮箱。</p>
          </div>
          <div class="contact-signal" :class="{ live: contactNotification.enabled && contactNotification.configured }">
            <span><i />{{ contactNotification.enabled && contactNotification.configured ? 'CONFIGURED' : 'STANDBY' }}</span>
            <strong>{{ overview.active }}</strong>
            <small>CONTACT ADDRESSES</small>
          </div>
        </article>

        <div class="contact-config-grid">
          <article class="paper-card webhook-console">
            <header><div><span class="eyebrow">ONE INBOX</span><h2>统一收件邮箱</h2></div><Bell /></header>
            <div class="webhook-state-row">
              <div><strong>Cloudflare 原生转发</strong><small>无需 Webhook、浏览器或付费通知服务，原邮件仍完整保留。</small></div>
              <span class="status-pill" :class="contactNotification.configured ? 'active' : 'pending'">{{ contactNotification.configured ? 'CONFIGURED' : 'VERIFY' }}</span>
            </div>
            <div class="secret-endpoint"><span>FORWARD DESTINATION</span><code>{{ contactNotification.forwardTo || '等待部署配置' }}</code><small>该地址在 Cloudflare 账户级验证一次后，适用于现在及以后动态新增的全部域名。</small></div>
          </article>

          <article class="paper-card delivery-ledger">
            <span class="eyebrow">FORWARDING POLICY</span>
            <h2>转发状态</h2>
            <div class="delivery-numbers">
              <div><strong>{{ overview.active }}</strong><span>覆盖域名</span></div>
              <div><strong>1</strong><span>统一收件箱</span></div>
            </div>
            <dl>
              <div><dt>配置状态</dt><dd>{{ contactNotification.configured ? '转发目标已配置' : '等待部署配置' }}</dd></div>
              <div><dt>保留策略</dt><dd>Webmail 与 R2 原信永久保留</dd></div>
              <div><dt>适用范围</dt><dd>全部现有及动态新增域名</dd></div>
            </dl>
          </article>
        </div>

        <article class="paper-card contact-address-book">
          <div class="card-title"><div><span class="eyebrow">DOMAIN DIRECTORY</span><h2>官方联系地址</h2></div><span>{{ domains.filter((domain) => domain.status === 'active').length }} 个在线地址</span></div>
          <div class="contact-address-row" v-for="domain in domains.filter((item) => item.status === 'active')" :key="domain.id">
            <span class="domain-avatar" :style="{ background: domain.accent }">{{ domain.zone[0]?.toUpperCase() }}</span>
            <div><strong>contact@{{ domain.mailboxDomain || domain.zone }}</strong><small>{{ domain.webmailHost }}</small></div>
            <span class="contact-route"><i /> WEBMAIL + EMAIL FORWARD</span>
          </div>
        </article>
      </section>

      <article v-else class="paper-card job-list">
        <div class="job-item" v-for="job in jobs" :key="job.id">
          <div class="job-marker" :class="job.status"><span /></div>
          <div class="job-main"><div><strong>{{ job.stage }}</strong><span>{{ job.status }}</span></div><el-progress :percentage="job.progress" :stroke-width="6" :show-text="false" /><small>{{ new Date(job.updatedAt).toLocaleString() }} · {{ job.id.slice(0, 8) }}</small><p v-if="job.errorMessage"><Warning /> {{ job.errorMessage }}</p></div>
        </div>
        <div v-if="!jobs.length" class="empty-state"><Refresh /><p>暂无装配任务</p></div>
      </article>
    </section>
  </div>

  <el-dialog v-model="accountDialog" title="接入 Cloudflare 账户" width="min(520px, 92vw)" class="mc-dialog">
    <form @submit.prevent="createAccount"><div class="form-grid"><label>显示名称<input v-model="accountForm.name" placeholder="Default Cloudflare" /></label><label>Account ID<input v-model="accountForm.accountId" autocomplete="off" placeholder="32 位账户 ID" /></label><label class="full">API Token<input v-model="accountForm.apiToken" type="password" autocomplete="new-password" placeholder="保存后仅显示已配置状态" /></label><label class="full">Resend API Key（可选）<input v-model="accountForm.resendApiKey" type="password" autocomplete="new-password" /></label></div></form>
    <template #footer><button class="quiet-button" @click="accountDialog = false">取消</button><button class="primary-button" :disabled="submitting" @click="createAccount">安全保存</button></template>
  </el-dialog>

  <el-dialog v-model="domainDialog" title="装配邮箱域名" width="min(560px, 92vw)" class="mc-dialog">
    <form @submit.prevent="createDomain"><div class="form-grid"><label class="full">Cloudflare 账户<select v-model="domainForm.accountId"><option v-for="account in accounts" :key="account.id" :value="account.id">{{ account.name }}</option></select></label><label>根域名<input v-model="domainForm.zone" placeholder="example.com" /></label><label>品牌名称<input v-model="domainForm.brandName" placeholder="默认为域名" /></label><label class="full">管理员登录邮箱（已有外部邮箱）<input v-model="domainForm.adminEmail" type="email" placeholder="owner@gmail.com" /></label><label>域内管理员地址<input v-model="domainForm.adminLocalPart" placeholder="admin" /></label><label>品牌强调色<input v-model="domainForm.accent" type="color" /></label></div></form>
    <p class="dialog-note">系统会先检查现有 MX 和 mail 子域冲突，再开始不可变装配步骤。</p>
    <template #footer><button class="quiet-button" @click="domainDialog = false">取消</button><button class="primary-button" :disabled="submitting || !accounts.length" @click="createDomain">开始装配</button></template>
  </el-dialog>

  <el-dialog v-model="integrationDialog" :title="editingIntegration ? '编辑邮箱集成策略' : '创建邮箱池集成'" width="min(650px, 94vw)" class="mc-dialog">
    <form @submit.prevent="saveIntegration"><div class="form-grid integration-form-grid">
      <label class="full">客户端名称<input v-model="integrationForm.name" placeholder="Any Auto Register" /></label>
      <label>默认域名策略<select v-model="integrationForm.defaultStrategy"><option value="round_robin">顺序轮询</option><option value="random">随机分配</option><option value="least_used">最少使用</option></select></label>
      <label v-if="!editingIntegration">Token 有效期<select v-model="integrationForm.expiresInDays"><option value="90">90 天</option><option value="365">1 年</option><option value="1095">3 年</option><option value="never">永不过期</option></select></label>
      <label class="full">域名白名单（可留空）<textarea v-model="integrationForm.allowedDomains" rows="4" placeholder="留空 = 所有当前及未来激活域名&#10;也支持 *.example.com" /><small>指定后只有匹配的域名可被分配。</small></label>
      <label class="full">域名黑名单（优先级最高）<textarea v-model="integrationForm.blockedDomains" rows="4" placeholder="blocked.example.com&#10;*.internal.example.com" /><small>支持精确域名、通配符子域及 *；命中后即使在白名单中也会拒绝。</small></label>
    </div></form>
    <p class="dialog-note">邮箱前缀由调用方按任务选择，可留空；留空时系统只生成随机本地名。</p>
    <template #footer><button class="quiet-button" @click="integrationDialog = false">取消</button><button class="primary-button" :disabled="submitting" @click="saveIntegration">{{ editingIntegration ? '保存策略' : '创建并签发 Token' }}</button></template>
  </el-dialog>
</template>
